import React,{useState,useEffect} from 'react';
import {supabase} from '../lib/supabase';
import SafeIcon from '../common/SafeIcon';
import {FiDownload,FiFileText,FiPrinter,FiRefreshCw,FiLoader,FiCheck,FiAlertCircle,FiToggleRight,FiToggleLeft,FiStar,FiTarget} from 'react-icons/fi';

const PDFFormularAdmin=()=> {
  const [menuData,setMenuData]=useState({
    burger: {
      pattyOptions: [],
      buns: [],
      toppings: [],
      sauces: [],
      sides: [],
      spicyLevels: []
    },
    pizza: {
      baseOptions: [],
      toppings: [],
      sauces: [],
      sides: [],
      spicyLevels: []
    }
  });
  const [loading,setLoading]=useState(true);
  const [generating,setGenerating]=useState(false);
  const [message,setMessage]=useState(null);
  const [activeProductType,setActiveProductType]=useState('burger');

  useEffect(()=> {
    fetchMenuOptions();
  },[]);

  const fetchMenuOptions=async ()=> {
    setLoading(true);
    try {
      // Fetch burger options
      const {data: burgerData,error: burgerError}=await supabase
        .from('menu_options_hb47x')
        .select('*')
        .eq('is_active',true)
        .eq('product_type','burger')
        .order('display_order',{ascending: true});

      if (burgerError) throw burgerError;

      // Fetch pizza options
      const {data: pizzaData,error: pizzaError}=await supabase
        .from('menu_options_hb47x')
        .select('*')
        .eq('is_active',true)
        .eq('product_type','pizza')
        .order('display_order',{ascending: true});

      if (pizzaError) throw pizzaError;

      // Group burger data
      const groupedBurger=burgerData.reduce((acc,item)=> {
        (acc[item.option_type]=acc[item.option_type] || []).push(item);
        return acc;
      },{});

      // Group pizza data
      const groupedPizza=pizzaData.reduce((acc,item)=> {
        (acc[item.option_type]=acc[item.option_type] || []).push(item);
        return acc;
      },{});

      setMenuData({
        burger: {
          pattyOptions: groupedBurger.patty || [],
          buns: groupedBurger.bun || [],
          toppings: groupedBurger.topping || [],
          sauces: groupedBurger.sauce || [],
          sides: groupedBurger.side || [],
          spicyLevels: groupedBurger.spicy_level || []
        },
        pizza: {
          baseOptions: groupedPizza.base || [],
          toppings: groupedPizza.topping || [],
          sauces: groupedPizza.sauce || [],
          sides: groupedPizza.side || [],
          spicyLevels: groupedPizza.spicy_level || []
        }
      });

      setMessage({type: 'success',text: 'Menüoptionen erfolgreich geladen.'});
    } catch (error) {
      console.error('Failed to fetch menu options:',error);
      setMessage({type: 'error',text: 'Fehler beim Laden der Menüoptionen.'});
    } finally {
      setLoading(false);
    }
  };

  const generatePDFLayout=async (productType)=> {
    setGenerating(true);
    setMessage(null);

    const isBurger=productType==='burger';
    const data=isBurger ? menuData.burger : menuData.pizza;
    const title=isBurger ? 'INDIVIDUAL-BURGER ERSTELLEN' : 'INDIVIDUAL-PIZZA ERSTELLEN';
    const fileName=`${isBurger ? 'Burger' : 'Pizza'}-Individual-Formular_${new Date().toISOString().split('T')[0]}.pdf`;

    try {
      const jsPDF=(await import('jspdf')).default;
      const pdf=new jsPDF({orientation: 'portrait',unit: 'mm',format: 'a4'});

      const pageWidth=210;
      const pageHeight=297;
      const margin=15;
      const contentWidth=pageWidth - 2 * margin;

      // Header
      pdf.setFillColor(30,30,30);// Dark gray
      pdf.rect(0,0,pageWidth,25,'F');

      pdf.setFont('helvetica','bold');
      pdf.setFontSize(18);
      pdf.setTextColor(255,255,255);
      pdf.text(title,pageWidth / 2,17,{align: 'center'});

      pdf.setFontSize(10);
      pdf.setTextColor(200,200,200);
      pdf.text('Gestalten Sie Ihren perfekten Burger/Pizza',pageWidth / 2,23,{align: 'center'});

      let y=35;

      // Helper to draw compact checkbox items
      const drawCompactCheckboxes=(title,items,options={})=> {
        const {columns=2,showPrice=true,showSpicy=false}=options;
        const columnWidth=contentWidth / columns;

        // Section header
        pdf.setFont('helvetica','bold');
        pdf.setFontSize(14);
        pdf.setTextColor(30,30,30);
        pdf.text(title,margin,y);
        y +=7;

        // Items in compact grid
        pdf.setFont('helvetica','normal');
        pdf.setFontSize(10);
        pdf.setTextColor(0,0,0);

        for (let i=0;i < items.length;i +=columns) {
          const rowItems=items.slice(i,i + columns);

          for (let col=0;col < rowItems.length;col++) {
            const item=rowItems[col];
            const x=margin + (col * (columnWidth + 5));

            // Checkbox
            pdf.setDrawColor(30,30,30);
            pdf.setLineWidth(1);
            pdf.rect(x,y,4,4);

            let displayText=item.name;
            if (showPrice && item.price > 0) {
              displayText +=` (+${item.price.toFixed(2)} CHF)`;
            }
            if (showSpicy && item.is_spicy) {
              displayText +=' 🔥';
            }

            pdf.text(displayText,x + 7,y + 3);
          }
          y +=8;

          // Check if we need a new page
          if (y > pageHeight - 30) {
            pdf.addPage();
            y=20;
          }
        }

        y +=5;
      };

      // 1. Patty/Base selection
      if (isBurger) {
        drawCompactCheckboxes('1. WÄHLEN SIE IHREN PATTY 🍔',data.pattyOptions,{columns: 2});
      } else {
        drawCompactCheckboxes('1. WÄHLEN SIE IHRE BASIS 🍕',data.baseOptions,{columns: 2});
      }

      // 2. Bun/Size
      if (isBurger) {
        drawCompactCheckboxes('2. BRÖTCHEN AUSWAHL',data.buns,{columns: 3});
      } else {
        drawCompactCheckboxes('2. GRÖSSE AUSWAHL',[
          {name: 'Klein (10")',price: 0},
          {name: 'Mittel (12")',price: 3.50},
          {name: 'Groß (14")',price: 6.00}
        ],{columns: 3});
      }

      // 3. Toppings
      drawCompactCheckboxes(isBurger ? '3. TOPPINGS 🥗' : '3. BELÄGE 🍅',data.toppings,{columns: 3});

      // 4. Sauces
      drawCompactCheckboxes(isBurger ? '4. SAUCEN 🍯' : '4. SAUCEN 🍯',data.sauces,{columns: 3,showSpicy: true});

      // 5. Spicy level (if any spicy sauces)
      if (data.sauces.some(s=> s.is_spicy)) {
        pdf.setFont('helvetica','bold');
        pdf.setFontSize(12);
        pdf.setTextColor(30,30,30);
        pdf.text('5. SCHÄRFE-LEVEL 🔥',margin,y);
        y +=6;

        pdf.setFont('helvetica','normal');
        pdf.setFontSize(10);
        pdf.setTextColor(0,0,0);
        pdf.text('🌶️1  🌶️🌶️2  🌶️🌶️🌶️3  🌶️🌶️🌶️🌶️4  🌶️🌶️🌶️🌶️🌶️5',margin,y);
        y +=6;
        pdf.text('Schärfegrad auswählen: ______',margin,y);
        y +=10;
      }

      // 6. Sides
      drawCompactCheckboxes('6. BEILAGEN 🍟',data.sides,{columns: 3});

      // 7. Side Sauces - Hinzugefügt
      drawCompactCheckboxes('7. SAUCEN FÜR BEILAGEN 🍯',data.sauces,{columns: 3,showSpicy: true});

      // 8. Contact information
      pdf.setFillColor(30,30,30);
      pdf.roundedRect(margin,y,contentWidth,35,3,3,'F');

      pdf.setFont('helvetica','bold');
      pdf.setFontSize(12);
      pdf.setTextColor(255,255,255);
      pdf.text('8. IHRE DATEN 📞',margin + 3,y + 6);
      y +=12;

      pdf.setFont('helvetica','normal');
      pdf.setFontSize(10);
      pdf.setTextColor(255,255,255);

      pdf.text('Name: ________________________',margin,y);
      pdf.text('Telefon: ________________',margin + 70,y);
      pdf.text('E-Mail: ________________________________',margin + 115,y);
      y +=10;

      // Special requests
      pdf.setFont('helvetica','bold');
      pdf.setFontSize(12);
      pdf.setTextColor(30,30,30);
      pdf.text('9. BESONDERE WÜNSCHE 💖',margin,y);
      y +=6;

      pdf.setFont('helvetica','normal');
      pdf.setFontSize(10);
      pdf.setTextColor(0,0,0);
      pdf.rect(margin,y,contentWidth,25);
      y +=30;

      // Newsletter consent
      pdf.setFont('helvetica','bold');
      pdf.setFontSize(12);
      pdf.setTextColor(30,30,30);
      pdf.text('10. NEWSLETTER & EINWILLIGUNGEN ✅',margin,y);
      y +=6;

      pdf.setFont('helvetica','normal');
      pdf.setFontSize(10);
      pdf.setTextColor(0,0,0);

      // Checkbox for newsletter
      pdf.setDrawColor(30,30,30);
      pdf.setLineWidth(1);
      pdf.rect(margin,y,4,4);
      pdf.text(' Ich möchte den Newsletter erhalten',margin + 7,y + 3);
      y +=8;

      // Checkbox for data consent
      pdf.setDrawColor(30,30,30);
      pdf.setLineWidth(1);
      pdf.rect(margin,y,4,4);
      pdf.text(' Ich stimme der Verarbeitung meiner Daten zu',margin + 7,y + 3);
      y +=10;

      pdf.save(fileName);
      setMessage({type: 'success',text: `PDF-Formular "${fileName}" wurde erfolgreich heruntergeladen!`});
    } catch (error) {
      console.error('Error generating PDF:',error);
      setMessage({type: 'error',text: `Fehler beim Generieren des PDFs: ${error.message}`});
    } finally {
      setGenerating(false);
    }
  };

  const previewFormular=(productType)=> {
    const isBurger=productType==='burger';
    const data=isBurger ? menuData.burger : menuData.pizza;
    const title=isBurger ? 'INDIVIDUAL-BURGER ERSTELLEN' : 'INDIVIDUAL-PIZZA ERSTELLEN';

    const previewContent=`
      <div class="page">
        <div class="header">
          <h1>${title}</h1>
          <p>Gestalten Sie Ihren perfekten Burger/Pizza</p>
        </div>
        
        <div class="content">
          <div class="section">
            <h3>1. ${isBurger ? 'WÄHLEN SIE IHREN PATTY 🍔' : 'WÄHLEN SIE IHRE BASIS 🍕'}</h3>
            <div class="items-container">
              ${(isBurger ? data.pattyOptions : data.baseOptions).map(item=> `
                <div class="item">
                  <span class="checkbox"></span>
                  <span class="item-text">${item.name} (${item.price.toFixed(2)} CHF)</span>
                </div>
              `).join('')}
            </div>
          </div>
          
          <div class="section">
            <h3>${isBurger ? '2. BRÖTCHEN AUSWAHL' : '2. GRÖSSE AUSWAHL'}</h3>
            <div class="items-container">
              ${isBurger ? data.buns.map(item=> `
                <div class="item">
                  <span class="checkbox"></span>
                  <span class="item-text">${item.name} ${item.price > 0 ? `(+${item.price.toFixed(2)} CHF)` : ''}</span>
                </div>
                `).join('') : `
                <div class="item">
                  <span class="checkbox"></span>
                  <span class="item-text">Klein (10")</span>
                </div>
                <div class="item">
                  <span class="checkbox"></span>
                  <span class="item-text">Mittel (12") (+3.50 CHF)</span>
                </div>
                <div class="item">
                  <span class="checkbox"></span>
                  <span class="item-text">Groß (14") (+6.00 CHF)</span>
                </div>
              `}
            </div>
          </div>
          
          <div class="section">
            <h3>3. ${isBurger ? 'TOPPINGS 🥗' : 'BELÄGE 🍅'}</h3>
            <div class="items-container">
              ${data.toppings.map(item=> `
                <div class="item">
                  <span class="checkbox"></span>
                  <span class="item-text">${item.name} (${item.price.toFixed(2)} CHF)</span>
                </div>
              `).join('')}
            </div>
          </div>
          
          <div class="section">
            <h3>4. SAUCEN 🍯</h3>
            <div class="items-container">
              ${data.sauces.map(item=> `
                <div class="item">
                  <span class="checkbox"></span>
                  <span class="item-text">${item.name} ${item.is_spicy ? '🔥' : ''} (${item.price.toFixed(2)} CHF)</span>
                </div>
              `).join('')}
            </div>
          </div>
          
          ${data.sauces.some(s=> s.is_spicy) ? `
            <div class="section">
              <h3>5. SCHÄRFE-LEVEL 🔥</h3>
              <div class="spicy-scale">
                <p>🌶️1  🌶️🌶️2  🌶️🌶️🌶️3  🌶️🌶️🌶️🌶️4  🌶️🌶️🌶️🌶️🌶️5</p>
                <p>Schärfegrad auswählen: ______</p>
              </div>
            </div>
          ` : ''}
          
          <div class="section">
            <h3>6. BEILAGEN 🍟</h3>
            <div class="items-container">
              ${data.sides.map(item=> `
                <div class="item">
                  <span class="checkbox"></span>
                  <span class="item-text">${item.name} (${item.price.toFixed(2)} CHF)</span>
                </div>
              `).join('')}
            </div>
          </div>
          
          <div class="section">
            <h3>7. SAUCEN FÜR BEILAGEN 🍯</h3>
            <div class="items-container">
              ${data.sauces.map(item=> `
                <div class="item">
                  <span class="checkbox"></span>
                  <span class="item-text">${item.name} ${item.is_spicy ? '🔥' : ''} (${item.price.toFixed(2)} CHF)</span>
                </div>
              `).join('')}
            </div>
          </div>
          
          <div class="section contact-section">
            <h3>8. IHRE DATEN 📞</h3>
            <div class="contact-form">
              <div class="form-row">
                <span>Name:</span>
                <div class="line"></div>
              </div>
              <div class="form-row">
                <span>Telefon:</span>
                <div class="line"></div>
              </div>
              <div class="form-row">
                <span>E-Mail:</span>
                <div class="line"></div>
              </div>
            </div>
          </div>
          
          <div class="section">
            <h3>9. BESONDERE WÜNSCHE 💖</h3>
            <div class="special-requests">
              <div class="line"></div>
            </div>
          </div>
          
          <div class="section">
            <h3>10. NEWSLETTER & EINWILLIGUNGEN ✅</h3>
            <div class="consent">
              <div class="checkbox-item">
                <span class="checkbox"></span>
                <span>Ich möchte den Newsletter erhalten</span>
              </div>
              <div class="checkbox-item">
                <span class="checkbox"></span>
                <span>Ich stimme der Verarbeitung meiner Daten zu</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    const styles=`
      body { 
        font-family: Arial,sans-serif; 
        margin: 0;
        padding: 0;
        background: #f5f5f5;
      }
      .page { 
        width: 210mm; 
        height: 297mm; 
        padding: 15mm; 
        margin: 10px auto; 
        background: white; 
        box-shadow: 0 0 10px rgba(0,0,0,0.1); 
        position: relative;
      }
      .header { 
        background: #1e1e1e;
        color: white;
        padding: 8px;
        text-align: center;
        border-radius: 5px;
        margin-bottom: 10px;
      }
      .header h1 {
        font-size: 18px;
        margin: 0 0 3px 0;
      }
      .header p {
        font-size: 10px;
        margin: 0;
        color: #ccc;
      }
      .content {
        margin-bottom: 15px;
      }
      .section {
        margin-bottom: 12px;
      }
      .section h3 {
        color: #1e1e1e;
        font-size: 14px;
        margin: 0 0 6px 0;
        padding-bottom: 2px;
        border-bottom: 1px solid #1e1e1e;
      }
      .items-container {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 4px;
      }
      .item {
        display: flex;
        align-items: center;
        font-size: 10px;
        padding: 2px 0;
      }
      .checkbox {
        width: 10px;
        height: 10px;
        border: 1px solid #1e1e1e;
        margin-right: 5px;
        flex-shrink: 0;
      }
      .item-text {
        line-height: 1.3;
      }
      .spicy-scale {
        font-size: 10px;
        padding: 4px 0;
      }
      .contact-section {
        background: #1e1e1e;
        padding: 6px;
        border-radius: 4px;
      }
      .contact-form {
        padding: 4px 0;
      }
      .form-row {
        display: flex;
        align-items: center;
        margin-bottom: 4px;
        font-size: 10px;
      }
      .form-row span {
        width: 45px;
        margin-right: 5px;
        font-weight: bold;
        color: white;
      }
      .line {
        flex: 1;
        height: 1px;
        background: white;
      }
      .special-requests {
        padding: 4px 0;
      }
      .special-requests .line {
        height: 25px;
        background: #000;
      }
      .consent {
        font-size: 10px;
      }
      .checkbox-item {
        display: flex;
        align-items: center;
        margin-bottom: 4px;
      }
      .checkbox-item .checkbox {
        width: 10px;
        height: 10px;
        border: 1px solid #1e1e1e;
        margin-right: 5px;
      }
    `;

    const previewWindow=window.open('','_blank','width=800,height=1000,scrollbars=yes');
    previewWindow.document.write(`
      <html>
        <head>
          <title>Vorschau: ${title}</title>
          <style>${styles}</style>
        </head>
        <body>${previewContent}</body>
      </html>
    `);
    previewWindow.document.close();
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center">
          <SafeIcon icon={FiFileText} className="mr-2 text-red-600" />
          Premium A4 Formular
        </h1>
        <button
          onClick={fetchMenuOptions}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center"
          disabled={loading}
        >
          <SafeIcon icon={loading ? FiLoader : FiRefreshCw} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
          Menü aktualisieren
        </button>
      </div>

      {message && (
        <div className={`mb-4 p-4 rounded-lg ${message.type==='success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center">
            <SafeIcon icon={message.type==='success' ? FiCheck : FiAlertCircle} className={`mr-2 ${message.type==='success' ? 'text-green-500' : 'text-red-500'}`} />
            <span className="font-medium">{message.text}</span>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-red-700 to-blue-900 text-white p-6">
          <h2 className="text-xl font-bold mb-2 flex items-center">
            <SafeIcon icon={FiTarget} className="mr-2 text-yellow-400" />
            Perfekt optimiertes A4 Formular
          </h2>
          <p className="opacity-90">
            Kompakt,übersichtlich und platzsparend gestaltet
          </p>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <SafeIcon icon={FiLoader} className="animate-spin mx-auto text-red-600 text-xl mb-2" />
              <p className="text-gray-500">Lade Menüoptionen...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Product Type Selector */}
              <div className="flex justify-center space-x-4 mb-6">
                <button
                  onClick={()=> setActiveProductType('burger')}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all flex items-center ${activeProductType==='burger' ? 'bg-red-600 text-white shadow-lg' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  <SafeIcon icon={activeProductType==='burger' ? FiToggleRight : FiToggleLeft} className="mr-2" />
                  🍔 Burger
                </button>
                <button
                  onClick={()=> setActiveProductType('pizza')}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all flex items-center ${activeProductType==='pizza' ? 'bg-red-600 text-white shadow-lg' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  <SafeIcon icon={activeProductType==='pizza' ? FiToggleRight : FiToggleLeft} className="mr-2" />
                  🍕 Pizza
                </button>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={()=> previewFormular(activeProductType)}
                  className="flex-1 px-6 py-3 bg-blue-800 text-white rounded-lg hover:bg-blue-900 flex items-center justify-center"
                  disabled={generating}
                >
                  <SafeIcon icon={FiPrinter} className="mr-2" />
                  {activeProductType==='burger' ? 'Burger' : 'Pizza'} Vorschau
                </button>
                <button
                  onClick={()=> generatePDFLayout(activeProductType)}
                  className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center"
                  disabled={generating}
                >
                  <SafeIcon icon={generating ? FiLoader : FiDownload} className={`mr-2 ${generating ? 'animate-spin' : ''}`} />
                  {generating ? 'PDF wird erstellt...' : `A4 ${activeProductType==='burger' ? '🍔 Burger' : '🍕 Pizza'} PDF`}
                </button>
              </div>

              {/* Information box */}
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h3 className="font-semibold text-red-800 mb-2 flex items-center">
                  <SafeIcon icon={FiStar} className="mr-2 text-yellow-500" />
                  Premium A4 Design Features:
                </h3>
                <ul className="text-sm text-red-700 space-y-1 list-disc pl-5">
                  <li><strong>Große Schrift & klare Linien:</strong> Perfekt lesbar auch bei schlechten Lichtverhältnissen</li>
                  <li><strong>Kompakt & platzsparend:</strong> Optimiert für A4 ohne Vergrößerung</li>
                  <li><strong>Übersichtlich strukturiert:</strong> Klare Abschnitte und kompakte Darstellung</li>
                  <li><strong>Professionell gestaltet:</strong> Restaurant-taugliches Design</li>
                  <li><strong>Druckoptimiert:</strong> Klare Kontraste und perfekte Lesbarkeit</li>
                  <li><strong>Multi-Produkt Support:</strong> Sowohl Burger als auch Pizza</li>
                  <li><strong>Datenschutz:</strong> Newsletter & Einwilligungen integriert</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PDFFormularAdmin;